/**
 * Utility functions for working with static filters (AndCriterion, OrCriterion, etc.)
 * These filters are used to automatically filter data in views
 */

import type {
  AndCriterionInput,
  AndCriterionOutput,
  OrCriterionInput,
  OrCriterionOutput,
  NotCriterionInput,
  NotCriterionOutput,
  EqualsCriterion,
  ContainsCriterion,
  GreaterThanCriterion,
  GreaterEqualThanCriterion,
  LessThanCriterion,
  LessEqualThanCriterion,
  CurrencyAmountCriterion,
  EntityCriterion,
  CalendarEventCriterion,
  ContractualPartyCriterion,
  MatterCriterion,
  OwnerCriterion,
  DateCriterion,
  ListAnyCriterion,
  ListAllCriterion,
  GaiaJsonValue,
} from '@/api';
import { isEnumValue } from '@/utils/enumUtils';
import {
  CRITERION_TYPES,
  getCriterionType,
  isStaticColumnType,
  isSpecialCriterionType,
  type CriterionTypeValue,
  type CriterionItem,
} from './criterionConstants';

/**
 * Union type for all possible criterion types (both Input and Output variants)
 */
type AllCriterionTypes =
  | AndCriterionInput
  | AndCriterionOutput
  | OrCriterionInput
  | OrCriterionOutput
  | NotCriterionInput
  | NotCriterionOutput
  | EqualsCriterion
  | ContainsCriterion
  | GreaterThanCriterion
  | GreaterEqualThanCriterion
  | LessThanCriterion
  | LessEqualThanCriterion
  | CurrencyAmountCriterion
  | EntityCriterion
  | CalendarEventCriterion
  | ContractualPartyCriterion
  | MatterCriterion
  | OwnerCriterion
  | DateCriterion
  | ListAnyCriterion
  | ListAllCriterion
  | null
  | undefined;

/**
 * Normalized criterion type - adds 'type' field to criterion types
 * This represents a criterion with both 'type' (internal) and 'criterion_type' (API) fields
 */
type NormalizedCriterion =
  | (AndCriterionInput & { type?: CriterionTypeValue })
  | (AndCriterionOutput & { type?: CriterionTypeValue })
  | (OrCriterionInput & { type?: CriterionTypeValue })
  | (OrCriterionOutput & { type?: CriterionTypeValue })
  | (NotCriterionInput & { type?: CriterionTypeValue })
  | (NotCriterionOutput & { type?: CriterionTypeValue })
  | (EqualsCriterion & { type?: CriterionTypeValue })
  | (ContainsCriterion & { type?: CriterionTypeValue })
  | (GreaterThanCriterion & { type?: CriterionTypeValue })
  | (GreaterEqualThanCriterion & { type?: CriterionTypeValue })
  | (LessThanCriterion & { type?: CriterionTypeValue })
  | (LessEqualThanCriterion & { type?: CriterionTypeValue })
  | (CurrencyAmountCriterion & { type?: CriterionTypeValue })
  | (EntityCriterion & { type?: CriterionTypeValue })
  | (CalendarEventCriterion & { type?: CriterionTypeValue })
  | (ContractualPartyCriterion & { type?: CriterionTypeValue })
  | (MatterCriterion & { type?: CriterionTypeValue })
  | (OwnerCriterion & { type?: CriterionTypeValue })
  | (DateCriterion & { type?: CriterionTypeValue })
  | (ListAnyCriterion & { type?: CriterionTypeValue })
  | (ListAllCriterion & { type?: CriterionTypeValue })
  | null
  | undefined;

/**
 * Normalize a single criterion.
 * Handles both API format (criterion_type) and internal format (type).
 * Recursively normalizes nested structures and complex fields.
 * Converts the structure to match what RecursiveQueryBuilder expects.
 */
export function normalizeCriterion(criterion: AllCriterionTypes): NormalizedCriterion {
  if (typeof criterion !== 'object' || criterion === null) return criterion;

  // Clone to avoid mutating original objects
  const normalized: Record<string, unknown> = { ...criterion };

  // 1. Unify type mapping
  // Prefer criterion_type (API) but keep type (Internal) for consistency
  // Note: MatterCriterion has a 'type' data field, so we need to check if 'type' is a string
  // (criterion type) vs an object (data field like EqualsCriterion)
  if ('criterion_type' in normalized && typeof normalized.criterion_type === 'string') {
    // If criterion_type exists and is a string, use it
    if (!('type' in normalized) || typeof normalized.type !== 'string') {
      normalized.type = normalized.criterion_type;
    }
  } else if ('type' in normalized && typeof normalized.type === 'string') {
    // Only treat 'type' as criterion type if it's a string (not a data field object)
    // Check if it's a valid criterion type value
    const typeValue = normalized.type;
    if (
      isEnumValue(CRITERION_TYPES, typeValue) &&
      (!('criterion_type' in normalized) || typeof normalized.criterion_type !== 'string')
    ) {
      normalized.criterion_type = normalized.type;
    }
  }

  const type =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    (typeof normalized.type === 'string' ? normalized.type : null) ||
    (typeof normalized.criterion_type === 'string' ? normalized.criterion_type : null);

  // 2. Special handling for static column criteria
  if (type && isStaticColumnType(type)) {
    const fields = getStaticColumnFields(type);
    for (const field of fields) {
      if (field.key in normalized && normalized[field.key]) {
        normalized[field.key] = normalizeCriterion(normalized[field.key] as AllCriterionTypes);
      }
    }
    return normalized;
  }

  // 3. Handle recursive structures (AND, OR, NOT, LIST_ANY, LIST_ALL)
  if ('criteria' in normalized && Array.isArray(normalized.criteria)) {
    normalized.criteria = normalized.criteria.map(normalizeCriterion);
  }

  if ('criterion' in normalized && normalized.criterion) {
    normalized.criterion = normalizeCriterion(normalized.criterion);
  }

  // 4. Handle Complex Field recursion (CurrencyAmount, Entity, etc.)
  if (type === CRITERION_TYPES.CURRENCY_AMOUNT) {
    if (normalized.amount) normalized.amount = normalizeCriterion(normalized.amount);
    if (normalized.iso_4217_alpha_3)
      normalized.iso_4217_alpha_3 = normalizeCriterion(normalized.iso_4217_alpha_3);
  } else if (type === CRITERION_TYPES.ENTITY) {
    if (normalized.name) normalized.name = normalizeCriterion(normalized.name);
    if (normalized.email) normalized.email = normalizeCriterion(normalized.email);
  } else if (type === CRITERION_TYPES.CALENDAR_EVENT && normalized.event_date)
    normalized.event_date = normalizeCriterion(normalized.event_date);

  return normalized;
}

/**
 * Normalize filter structure from API format to internal format
 */
export function normalizeFilterFromApi(
  criterion:
    AndCriterionOutput | OrCriterionOutput | AndCriterionInput | OrCriterionInput | null | undefined
): AndCriterionInput | OrCriterionInput | null {
  if (!criterion) return null;
  return normalizeCriterion(criterion) as AndCriterionInput | OrCriterionInput;
}

/**
 * Check if a criterion is empty (no actual filter conditions)
 */
export function isEmptyCriterion(
  criterion:
    AndCriterionInput | AndCriterionOutput | OrCriterionInput | OrCriterionOutput | null | undefined
): boolean {
  if (!criterion) return true;
  if ('criteria' in criterion) {
    return criterion.criteria.length === 0;
  }
  return false;
}

/**
 * Check if a filter has any active conditions
 */
export function hasActiveFilters(
  filters: AndCriterionInput | AndCriterionOutput | null | undefined
): boolean {
  if (!filters || isEmptyCriterion(filters)) return false;

  // Recursively check if any criteria exist
  function hasActiveCriteria(
    criterion: AndCriterionInput | AndCriterionOutput | OrCriterionInput | OrCriterionOutput
  ): boolean {
    if ('criteria' in criterion) {
      if (criterion.criteria.length === 0) return false;

      // Check if any nested criterion is active
      return criterion.criteria.some(c => {
        // Check for AND/OR criteria
        if (isAndCriterion(c) || isOrCriterion(c)) {
          return hasActiveCriteria(c);
        }
        // Check for Equals or Contains criterion
        if (isEqualsCriterion(c) || isContainsCriterion(c)) {
          const value = c.value;
          if (Array.isArray(value)) {
            return value.length > 0;
          }
          // `== null` is a real "is empty" filter (e.g. "No date"), not an unset rule.
          if (value === null) return isEqualsCriterion(c);
          return value !== '';
        }
        // Check for static column criteria
        if (isStaticColumnCriterion(c)) {
          // Static column criteria are active if they have at least one field with a value
          const fieldAndValue = getStaticColumnCriterionFieldAndValue(c);
          return fieldAndValue !== null && fieldAndValue.value !== '';
        }
        return true; // NotCriterion or other types
      });
    }
    return false;
  }

  return hasActiveCriteria(filters);
}

/**
 * Extract filter values from a criterion for display
 * Returns an array of values that would be used in filtering
 */
export function extractFilterValues(
  criterion:
    | AndCriterionInput
    | AndCriterionOutput
    | OrCriterionInput
    | OrCriterionOutput
    | NotCriterionInput
    | NotCriterionOutput
    | null
    | undefined
): GaiaJsonValue[] {
  const values: GaiaJsonValue[] = [];

  if (!criterion) return values;

  // Check if it's empty — a NOT criterion always carries an inner criterion,
  // so only plain criteria can be empty.
  if (!isNotCriterion(criterion) && isEmptyCriterion(criterion)) {
    return values;
  }

  function extractFromCriterion(c: unknown): void {
    if (!c || typeof c !== 'object') return;

    // Handle NOT criterion - extract from inner criterion
    if (isNotCriterion(c)) {
      const innerCriterion = (c as NotCriterionInput | NotCriterionOutput).criterion;
      if (isEqualsCriterion(innerCriterion) || isContainsCriterion(innerCriterion)) {
        const criterionValue = innerCriterion.value;
        if (Array.isArray(criterionValue)) {
          values.push(...criterionValue);
        } else {
          values.push(criterionValue);
        }
      } else if (isStaticColumnCriterion(innerCriterion)) {
        // Extract from static column criterion inside NOT
        extractFromStaticColumnCriterion(innerCriterion);
      } else {
        // Recursively extract from nested criterion
        extractFromCriterion(innerCriterion);
      }
      return;
    }

    // Handle static column criteria
    if (isStaticColumnCriterion(c)) {
      extractFromStaticColumnCriterion(c);
      return;
    }

    // Handle list criteria (list_any, list_all) - extract from inner criterion
    if (isListAnyCriterion(c) || isListAllCriterion(c)) {
      extractFromCriterion(c.criterion);
      return;
    }

    // Handle AND/OR criteria
    if (isAndCriterion(c) || isOrCriterion(c)) {
      for (const item of c.criteria) {
        extractFromCriterion(item);
      }
      return;
    }

    // Handle direct rule criteria (including comparison operators)
    if (
      isEqualsCriterion(c) ||
      isContainsCriterion(c) ||
      isGreaterThanCriterion(c) ||
      isGreaterEqualThanCriterion(c) ||
      isLessThanCriterion(c) ||
      isLessEqualThanCriterion(c)
    ) {
      const criterionValue = c.value;
      if (Array.isArray(criterionValue)) {
        values.push(...criterionValue);
      } else if (criterionValue === null) {
        // `== null` is a real "is empty" filter (e.g. "No date"), so it counts.
        if (isEqualsCriterion(c)) values.push(null);
      } else if (criterionValue !== '') {
        // Handle comma-separated values for ContainsCriterion (legacy/static support)
        if (
          isContainsCriterion(c) &&
          typeof criterionValue === 'string' &&
          criterionValue.includes(',')
        ) {
          const splitValues = criterionValue
            .split(',')
            .map(v => v.trim())
            .filter(v => v);
          values.push(...splitValues);
        } else {
          values.push(criterionValue);
        }
      }
    }

    // Handle special criterion types (CurrencyAmount, Entity, CalendarEvent)
    // For these, we count them as having 1 filter active even though they don't have simple values
    if (isCurrencyAmountCriterion(c)) {
      const currencyCriterion = c;
      if (currencyCriterion.amount !== null && currencyCriterion.amount !== undefined) {
        values.push(currencyCriterion.amount);
      }
    } else if (isEntityCriterion(c)) {
      const entityCriterion = c;
      if (entityCriterion.name !== null && entityCriterion.name !== undefined) {
        values.push(entityCriterion.name);
      }
    } else if (isCalendarEventCriterion(c)) {
      const calendarCriterion = c;
      if (calendarCriterion.event_date !== null && calendarCriterion.event_date !== undefined) {
        values.push(calendarCriterion.event_date);
      }
    }
  }

  // Helper function to extract values from static column criteria
  function extractFromStaticColumnCriterion(
    criterion: ContractualPartyCriterion | MatterCriterion | OwnerCriterion | DateCriterion
  ): void {
    const crit = criterion as Record<string, unknown>;
    const fields = getStaticColumnFields(
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
      getStaticColumnTypeFromCriterion(criterion) || CRITERION_TYPES.CONTRACTUAL_PARTY
    );

    for (const fieldInfo of fields) {
      const fieldValue = crit[fieldInfo.key];
      if (fieldValue) {
        extractFromCriterion(fieldValue);
      }
    }
  }

  extractFromCriterion(criterion);
  return values;
}

/**
 * Helper to create a criterion object with both 'type' and 'criterion_type' fields
 * for API compatibility
 */
function createCriterionObject(
  type: CriterionTypeValue,
  value: string | number | (string | number)[]
):
  | (EqualsCriterion & { type: CRITERION_TYPES.EQUALS })
  | (ContainsCriterion & { type: CRITERION_TYPES.CONTAINS })
  | (GreaterThanCriterion & { type: CRITERION_TYPES.GREATER_THAN })
  | (GreaterEqualThanCriterion & { type: CRITERION_TYPES.GREATER_EQUAL })
  | (LessThanCriterion & { type: CRITERION_TYPES.LESS_THAN })
  | (LessEqualThanCriterion & { type: CRITERION_TYPES.LESS_EQUAL }) {
  return {
    type,
    criterion_type: type, // Also set criterion_type for API compatibility
    value,
  } as
    | (EqualsCriterion & { type: CRITERION_TYPES.EQUALS })
    | (ContainsCriterion & { type: CRITERION_TYPES.CONTAINS })
    | (GreaterThanCriterion & { type: CRITERION_TYPES.GREATER_THAN })
    | (GreaterEqualThanCriterion & { type: CRITERION_TYPES.GREATER_EQUAL })
    | (LessThanCriterion & { type: CRITERION_TYPES.LESS_THAN })
    | (LessEqualThanCriterion & { type: CRITERION_TYPES.LESS_EQUAL });
}

/**
 * Create a simple ContainsCriterion (IN filter) from an array of values
 * This is the most common filter type for static columns
 * Note: API expects 'criterion_type' field, but we use 'type' internally
 * The API client should handle the conversion, but we ensure both are set for compatibility
 */
export function createContainsFilter(values: (string | number)[]): ContainsCriterion {
  return createCriterionObject(
    CRITERION_TYPES.CONTAINS,
    values.length === 1 ? values[0] : values
  ) as ContainsCriterion;
}

/**
 * Create an EqualsCriterion (== filter) from a single value
 * Note: API expects 'criterion_type' field, but we use 'type' internally
 * The API client should handle the conversion, but we ensure both are set for compatibility
 */
export function createEqualsFilter(value: string | number): EqualsCriterion {
  return createCriterionObject(CRITERION_TYPES.EQUALS, value) as EqualsCriterion;
}

/**
 * Create a NotCriterion (NOT filter) from a criterion
 */
export function createNotFilter(criterion: EqualsCriterion | ContainsCriterion): {
  criterion: EqualsCriterion | ContainsCriterion;
} {
  return {
    criterion,
  };
}

/**
 * Create an AndCriterion with ContainsCriterion filters
 */
export function createAndFilter(values: (string | number)[]): AndCriterionInput {
  return {
    criterion_type: CRITERION_TYPES.AND,
    criteria: [createContainsFilter(values)],
  };
}

/**
 * Create an OrCriterion with multiple ContainsCriterion filters
 * Each array in the values array becomes a separate ContainsCriterion
 */
export function createOrFilter(valueGroups: (string | number)[][]): OrCriterionInput {
  return {
    criterion_type: CRITERION_TYPES.OR,
    criteria: valueGroups.map(group => createContainsFilter(group)),
  };
}

/**
 * Create a complex filter: (Group1 AND Group2) OR (Group3 AND Group4)
 * where each group is an array of values that should be ANDed together
 */
export function createComplexFilter(
  valueGroups: (string | number)[][]
): AndCriterionInput | OrCriterionInput {
  if (valueGroups.length === 0) {
    return { criterion_type: CRITERION_TYPES.AND, criteria: [] };
  }

  if (valueGroups.length === 1) {
    // Single group - just AND them together
    return createAndFilter(valueGroups[0]);
  }

  // Multiple groups - OR them together, each group is ANDed internally
  const orCriteria = valueGroups.map(group => createContainsFilter(group));
  return {
    criterion_type: CRITERION_TYPES.OR,
    criteria: orCriteria,
  };
}

/**
 * Helper to check if an object is a valid object (not null/undefined)
 */
function isValidObject(obj: unknown): obj is Record<string, unknown> {
  return typeof obj === 'object' && obj !== null;
}

/**
 * Type guard: Check if object is AndCriterionInput
 * Handles both 'type' (internal) and 'criterion_type' (API) fields
 */
export function isAndCriterion(obj: unknown): obj is AndCriterionInput {
  if (!isValidObject(obj) || !('criteria' in obj) || !Array.isArray(obj.criteria)) {
    return false;
  }
  return getCriterionType(obj) === CRITERION_TYPES.AND;
}

/**
 * Type guard: Check if object is OrCriterionInput
 * Handles both 'type' (internal) and 'criterion_type' (API) fields
 */
export function isOrCriterion(obj: unknown): obj is OrCriterionInput {
  if (!isValidObject(obj) || !('criteria' in obj) || !Array.isArray(obj.criteria)) {
    return false;
  }
  return getCriterionType(obj) === CRITERION_TYPES.OR;
}

/**
 * Type guard: Check if object is EqualsCriterion
 * Handles both 'type' (internal) and 'criterion_type' (API) fields
 */
export function isEqualsCriterion(obj: unknown): obj is EqualsCriterion {
  if (!isValidObject(obj) || !('value' in obj)) return false;
  return getCriterionType(obj) === CRITERION_TYPES.EQUALS;
}

/**
 * Type guard: Check if object is ContainsCriterion
 * Handles both 'type' (internal) and 'criterion_type' (API) fields
 */
export function isContainsCriterion(obj: unknown): obj is ContainsCriterion {
  if (!isValidObject(obj) || !('value' in obj)) return false;
  return getCriterionType(obj) === CRITERION_TYPES.CONTAINS;
}

/**
 * Type guard: Check if object is NotCriterionInput
 * Handles both 'type' (internal) and 'criterion_type' (API) fields
 */
export function isNotCriterion(obj: unknown): obj is { criterion: unknown } {
  if (!isValidObject(obj) || !('criterion' in obj)) return false;
  return getCriterionType(obj) === CRITERION_TYPES.NOT;
}

/**
 * Type guard: Check if object is ContractualPartyCriterion
 */
export function isContractualPartyCriterion(obj: unknown): obj is ContractualPartyCriterion {
  if (!isValidObject(obj)) return false;
  const criterionType = getCriterionType(obj);
  return criterionType === CRITERION_TYPES.CONTRACTUAL_PARTY;
}

/**
 * Type guard: Check if object is MatterCriterion
 */
export function isMatterCriterion(obj: unknown): obj is MatterCriterion {
  if (!isValidObject(obj)) return false;
  const criterionType = getCriterionType(obj);
  return criterionType === CRITERION_TYPES.MATTER;
}

/**
 * Type guard: Check if object is OwnerCriterion
 */
export function isOwnerCriterion(obj: unknown): obj is OwnerCriterion {
  if (!isValidObject(obj)) return false;
  const criterionType = getCriterionType(obj);
  return criterionType === CRITERION_TYPES.OWNER;
}

/**
 * Type guard: Check if object is DateCriterion
 */
export function isDateCriterion(obj: unknown): obj is DateCriterion {
  if (!isValidObject(obj)) return false;
  return getCriterionType(obj) === CRITERION_TYPES.DATE;
}

/**
 * Type guard: Check if object is a static column criterion (ContractualParty, Matter, Owner or Date)
 */
export function isStaticColumnCriterion(
  obj: unknown
): obj is ContractualPartyCriterion | MatterCriterion | OwnerCriterion | DateCriterion {
  return (
    isContractualPartyCriterion(obj) ||
    isMatterCriterion(obj) ||
    isOwnerCriterion(obj) ||
    isDateCriterion(obj)
  );
}

/**
 * Type guard: Check if object is GreaterThanCriterion
 * Handles both 'type' (internal) and 'criterion_type' (API) fields
 */
export function isGreaterThanCriterion(obj: unknown): obj is GreaterThanCriterion {
  if (!isValidObject(obj) || !('value' in obj)) return false;
  return getCriterionType(obj) === CRITERION_TYPES.GREATER_THAN;
}

/**
 * Type guard: Check if object is GreaterEqualThanCriterion
 * Handles both 'type' (internal) and 'criterion_type' (API) fields
 */
export function isGreaterEqualThanCriterion(obj: unknown): obj is GreaterEqualThanCriterion {
  if (!isValidObject(obj) || !('value' in obj)) return false;
  return getCriterionType(obj) === CRITERION_TYPES.GREATER_EQUAL;
}

/**
 * Type guard: Check if object is LessThanCriterion
 * Handles both 'type' (internal) and 'criterion_type' (API) fields
 */
export function isLessThanCriterion(obj: unknown): obj is LessThanCriterion {
  if (!isValidObject(obj) || !('value' in obj)) return false;
  return getCriterionType(obj) === CRITERION_TYPES.LESS_THAN;
}

/**
 * Type guard: Check if object is LessEqualThanCriterion
 * Handles both 'type' (internal) and 'criterion_type' (API) fields
 */
export function isLessEqualThanCriterion(obj: unknown): obj is LessEqualThanCriterion {
  if (!isValidObject(obj) || !('value' in obj)) return false;
  return getCriterionType(obj) === CRITERION_TYPES.LESS_EQUAL;
}

/**
 * Type guard: Check if object is CurrencyAmountCriterion
 */
export function isCurrencyAmountCriterion(obj: unknown): obj is CurrencyAmountCriterion {
  if (!isValidObject(obj)) return false;
  return getCriterionType(obj) === CRITERION_TYPES.CURRENCY_AMOUNT;
}

/**
 * Type guard: Check if object is EntityCriterion
 */
export function isEntityCriterion(obj: unknown): obj is EntityCriterion {
  if (!isValidObject(obj)) return false;
  return getCriterionType(obj) === CRITERION_TYPES.ENTITY;
}

/**
 * Type guard: Check if object is CalendarEventCriterion
 */
export function isCalendarEventCriterion(obj: unknown): obj is CalendarEventCriterion {
  if (!isValidObject(obj)) return false;
  return getCriterionType(obj) === CRITERION_TYPES.CALENDAR_EVENT;
}

/**
 * Type guard: Check if object is ListAnyCriterion
 * Handles both 'type' (internal) and 'criterion_type' (API) fields
 */
export function isListAnyCriterion(obj: unknown): obj is ListAnyCriterion {
  if (!isValidObject(obj) || !('criterion' in obj)) return false;
  return getCriterionType(obj) === CRITERION_TYPES.LIST_ANY;
}

/**
 * Type guard: Check if object is ListAllCriterion
 * Handles both 'type' (internal) and 'criterion_type' (API) fields
 */
export function isListAllCriterion(obj: unknown): obj is ListAllCriterion {
  if (!isValidObject(obj) || !('criterion' in obj)) return false;
  return getCriterionType(obj) === CRITERION_TYPES.LIST_ALL;
}

/**
 * Type guard: Check if object is any list criterion (list_any or list_all)
 */
export function isListCriterion(obj: unknown): obj is ListAnyCriterion | ListAllCriterion {
  return isListAnyCriterion(obj) || isListAllCriterion(obj);
}

/**
 * Type-safe field definitions extracted from API types
 * These match the actual field names in ContractualPartyCriterion, MatterCriterion and OwnerCriterion.
 */
type StaticColumnFieldMap = {
  contractual_party: StaticColumnField<keyof ContractualPartyCriterion>[];
  matter: StaticColumnField<keyof MatterCriterion>[];
  owner: StaticColumnField<keyof OwnerCriterion>[];
  date: StaticColumnField<keyof DateCriterion>[];
};

/**
 * One filterable field of a static column criterion. `kind` marks the fields
 * that aren't strings — a `TimeCriterion` date or a `BoolCriterion` flag — so
 * the rule input offers comparisons or a yes/no pick instead of free text.
 */
export interface StaticColumnField<TKey extends string = string> {
  key: TKey;
  label: string;
  kind?: 'date' | 'boolean';
}

export type StaticCriterionColumnType = keyof StaticColumnFieldMap;

/**
 * Field definitions for static column criteria
 * Keys are extracted from API types to ensure type safety
 * Labels are human-readable names based on API documentation comments
 */
const STATIC_COLUMN_FIELD_MAP: StaticColumnFieldMap = {
  // The backend filters a party on these four only — no kind-specific fields.
  contractual_party: [
    { key: 'name', label: 'Name' },
    { key: 'kind', label: 'Kind' },
    { key: 'city', label: 'City' },
    { key: 'iso_3166_alpha_3', label: 'Country Code (3 letters)' },
  ],
  matter: [
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
    { key: 'type', label: 'Type' },
  ],
  owner: [
    { key: 'full_name', label: 'Full Name' },
    { key: 'email', label: 'Email' },
  ],
  date: [
    { key: 'date', label: 'Date', kind: 'date' },
    { key: 'description', label: 'Description' },
  ],
};

/**
 * Field definitions for special complex criteria
 */
const SPECIAL_CRITERION_FIELD_MAP: Record<string, { key: string; label: string }[] | undefined> = {
  [CRITERION_TYPES.CURRENCY_AMOUNT]: [
    { key: 'amount', label: 'Amount' },
    { key: 'iso_4217_alpha_3', label: 'Currency Code (3 letters)' },
  ],
  [CRITERION_TYPES.ENTITY]: [
    { key: 'name', label: 'Name' },
    { key: 'form_of_entity', label: 'Form' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
  ],
  [CRITERION_TYPES.CALENDAR_EVENT]: [
    { key: 'event_date', label: 'Event Date' },
    { key: 'recurrence_type', label: 'Recurrence Type' },
    { key: 'recurrence_count', label: 'Recurrence Count' },
    { key: 'recurrence_until_date', label: 'Recurrence Until Date' },
    { key: 'recurrence_frequency', label: 'Recurrence Frequency' },
    { key: 'recurrence_interval', label: 'Recurrence Interval' },
  ],
};

/**
 * Get available fields for a static column type
 * Returns fields with type-safe keys extracted from API types
 */
export function getStaticColumnFields(columnType: StaticCriterionColumnType): StaticColumnField[] {
  const fields: StaticColumnField[] = STATIC_COLUMN_FIELD_MAP[columnType];
  // Convert to string keys for runtime use (TypeScript keyof types are preserved)
  return fields.map(f => ({ key: f.key, label: f.label, kind: f.kind }));
}

/**
 * Get available fields for a special complex criterion type
 */
export function getSpecialCriterionFields(type: string): StaticColumnField[] {
  return SPECIAL_CRITERION_FIELD_MAP[type] ?? [];
}

/**
 * Get available fields for a field-aware rule (static column or special criterion)
 */
export function getFieldsForRule(rule: CriterionItem): StaticColumnField[] {
  const type = getCriterionType(rule);
  if (!type) return [];
  if (isStaticColumnType(type)) return getStaticColumnFields(type);
  if (isSpecialCriterionType(type)) return getSpecialCriterionFields(type);
  return [];
}

/**
 * Get the static column type from a criterion
 */
export function getStaticColumnTypeFromCriterion(
  criterion: unknown
): StaticCriterionColumnType | null {
  if (isContractualPartyCriterion(criterion)) return 'contractual_party';
  if (isMatterCriterion(criterion)) return 'matter';
  if (isOwnerCriterion(criterion)) return 'owner';
  if (isDateCriterion(criterion)) return 'date';
  return null;
}

/**
 * Generalized helper to get current field, operator and value from any object-based criterion
 */
export function getObjectCriterionState(criterion: unknown): {
  field: string;
  operator: CriterionTypeValue;
  value: string | number;
} | null {
  if (!isValidObject(criterion)) return null;

  const type = getCriterionType(criterion);
  if (!type) return null;

  const crit = criterion;
  let fields: { key: string; label: string }[] = [];

  if (isStaticColumnType(type)) {
    fields = getStaticColumnFields(type);
  } else if (isSpecialCriterionType(type)) {
    fields = getSpecialCriterionFields(type);
  } else {
    return null;
  }

  // Iterate over fields and find the one that has an active criterion
  for (const fieldInfo of fields) {
    const fieldValue = crit[fieldInfo.key];
    if (fieldValue && typeof fieldValue === 'object') {
      const fieldOp = getCriterionType(fieldValue);
      if (fieldOp && 'value' in fieldValue) {
        // Runtime values can also be booleans or absent — see normalization below.
        const value = (fieldValue as { value: string | number | boolean | null | undefined }).value;
        if (value === null || value === undefined) continue;

        const normalizedValue: string | number = typeof value === 'boolean' ? String(value) : value;

        return {
          field: fieldInfo.key,
          operator: fieldOp,
          value: normalizedValue,
        };
      }
    }
  }

  return null;
}

/**
 * Legacy alias for getObjectCriterionState
 */
export function getStaticColumnCriterionFieldAndValue(criterion: CriterionItem): {
  field: string;
  value: string | number;
  ruleType: CriterionTypeValue;
} | null {
  const state = getObjectCriterionState(criterion);
  if (!state) return null;
  return {
    field: state.field,
    value: state.value,
    ruleType: state.operator,
  };
}

/**
 * Validate filter structure
 * Returns validation result with error message if invalid
 */
export function validateFilters(filters: AndCriterionInput | null | undefined): {
  valid: boolean;
  error?: string;
} {
  if (!filters) {
    return { valid: true }; // null/undefined is valid (no filters)
  }

  // Must be AndCriterion at top level
  if (!isAndCriterion(filters)) {
    return {
      valid: false,
      error: 'Top-level filter must be an AndCriterion (type: "and")',
    };
  }

  // Check criteria array
  if (!Array.isArray(filters.criteria)) {
    return {
      valid: false,
      error: 'Criteria must be an array',
    };
  }

  // Recursively validate nested criteria
  function validateCriterion(criterion: unknown): { valid: boolean; error?: string } {
    if (isAndCriterion(criterion) || isOrCriterion(criterion)) {
      if (!Array.isArray(criterion.criteria)) {
        return {
          valid: false,
          error: 'Nested AndCriterion/OrCriterion must have criteria array',
        };
      }
      for (const item of criterion.criteria) {
        const result = validateCriterion(item);
        if (!result.valid) return result;
      }
      return { valid: true };
    }

    if (
      isEqualsCriterion(criterion) ||
      isContainsCriterion(criterion) ||
      isGreaterThanCriterion(criterion) ||
      isGreaterEqualThanCriterion(criterion) ||
      isLessThanCriterion(criterion) ||
      isLessEqualThanCriterion(criterion)
    ) {
      // `== null` is a deliberate "is empty" filter (e.g. "No date"); an unset
      // rule holds '' instead, so the two never collide.
      if (isEqualsCriterion(criterion) && 'value' in criterion && criterion.value === null) {
        return { valid: true };
      }
      // Check if value exists and is not null/undefined/empty string
      // Allow 0 as a valid value (it's falsy but valid)
      if (
        !('value' in criterion) ||
        criterion.value === null ||
        (typeof criterion.value === 'string' && criterion.value.trim() === '')
      ) {
        return {
          valid: false,
          error: 'Criterion must have a value',
        };
      }
      return { valid: true };
    }

    if (
      isStaticColumnCriterion(criterion) ||
      isCurrencyAmountCriterion(criterion) ||
      isEntityCriterion(criterion) ||
      isCalendarEventCriterion(criterion)
    ) {
      // Object-based criteria must have at least one field with a valid criterion
      const crit = criterion as Record<string, CriterionItem | undefined>;
      const type = getCriterionType(criterion);

      const fields = isStaticColumnType(type)
        ? getStaticColumnFields(type)
        : getSpecialCriterionFields(type as string);

      let hasValidField = false;
      for (const fieldInfo of fields) {
        const fieldCriterion = crit[fieldInfo.key];
        if (fieldCriterion) {
          const result = validateCriterion(fieldCriterion);
          if (!result.valid) return result;
          hasValidField = true;
        }
      }
      if (!hasValidField) {
        return {
          valid: false,
          error: `${type} criterion must have at least one field with a filter`,
        };
      }
      return { valid: true };
    }

    if (isNotCriterion(criterion)) {
      if (!('criterion' in criterion) || !criterion.criterion) {
        return {
          valid: false,
          error: 'NotCriterion must have a criterion property',
        };
      }
      return validateCriterion(criterion.criterion);
    }

    // Validate list criteria (list_any, list_all)
    if (isListAnyCriterion(criterion) || isListAllCriterion(criterion)) {
      return validateCriterion(criterion.criterion);
    }

    return {
      valid: false,
      error: 'Invalid criterion structure',
    };
  }

  // Validate all criteria
  for (const criterion of filters.criteria) {
    const result = validateCriterion(criterion);
    if (!result.valid) return result;
  }

  return { valid: true };
}
